import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styles from "./Login.module.css";

interface LoginProps {
  autoStart?: boolean;
  mockMode?: boolean;
}

/**
 * Logout lands here. Sends the user straight to Auth0 instead of showing a
 * sign-in button; Auth0's own page is the login screen.
 */
export const Login: React.FC<LoginProps> = ({
  autoStart = true,
  mockMode = false,
}) => {
  const { isLoading, isAuthenticated, error: auth0Error, loginWithRedirect } = useAuth0();
  const { search } = useLocation();
  const redirectStarted = useRef(false);
  const [error, setError] = useState(false);
  const [isLogoutLanding] = useState(() => {
    return sessionStorage.getItem("oneportal_logout_complete") === "true";
  });
  const callbackParams = new URLSearchParams(search);
  const isAuth0Callback = Boolean(auth0Error) ||
    callbackParams.has("error") ||
    (callbackParams.has("code") && callbackParams.has("state"));

  useEffect(() => {
    if (isLogoutLanding) {
      sessionStorage.removeItem("oneportal_logout_complete");
    }
  }, [isLogoutLanding]);

  const startLogin = (forceLogin = isLogoutLanding) => {
    if (
      isLoading ||
      isAuthenticated ||
      isAuth0Callback ||
      redirectStarted.current
    ) return;

    redirectStarted.current = true;
    setError(false);
    void loginWithRedirect(
      forceLogin ? { authorizationParams: { prompt: "login" } } : undefined,
    ).catch((err: unknown) => {
      redirectStarted.current = false;
      setError(true);
      console.error("Auth0 login error:", err);
    });
  };

  useEffect(() => {
    if (autoStart && !mockMode) startLogin();
  }, [autoStart, mockMode, isLoading, isAuthenticated, isAuth0Callback, isLogoutLanding, loginWithRedirect]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (mockMode) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginContent} role="status">
          Mock authentication is enabled, but no local session is available.
        </div>
      </div>
    );
  }

  if (isAuth0Callback || !error) return null;

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginContent} role="alert">
        <p>Unable to start sign-in. Check your connection and try again.</p>
        <button
          type="button"
          className={styles.loginButton}
          onClick={() => startLogin()}
        >
          Retry sign-in
        </button>
      </div>
    </div>
  );
};

